import React from 'react';
import { 
  Home, 
  ChevronLeft, 
  ChevronRight, 
  Sparkles, 
  MessageCircle,
  Code2,
  TrendingUp,
  Briefcase,
  Calculator,
  Mail,
  Zap,
  Cpu,
  ShieldCheck,
  HelpCircle
} from 'lucide-react';
import { AppPage, Language } from '../types';
import { WHATSAPP_PHONE_DISPLAY, createWhatsAppUrl } from '../utils/whatsapp';
import { SorenaLogo } from './SorenaLogo';

interface PageHeaderBannerProps {
  lang: Language;
  currentPage: AppPage;
  onNavigate: (page: AppPage) => void;
  title: string;
  subtitle: string;
  badge: string;
  icon: React.ElementType;
}

export const PageHeaderBanner: React.FC<PageHeaderBannerProps> = ({
  lang,
  currentPage,
  onNavigate,
  title,
  subtitle,
  badge,
  icon: Icon
}) => {
  const isRtl = lang === 'fa';
  const Chevron = isRtl ? ChevronLeft : ChevronRight;

  const quickPages: { id: AppPage; label: string; icon: React.ElementType }[] = [
    { id: 'home', label: lang === 'fa' ? 'صفحه اصلی' : 'Home', icon: Home },
    { id: 'campaign', label: lang === 'fa' ? 'کمپین تخفیف (۷۰٪)' : 'Campaign (70%)', icon: Sparkles },
    { id: 'services', label: lang === 'fa' ? 'خدمات طراحی وب' : 'Web Services', icon: Code2 },
    { id: 'algo-trading', label: lang === 'fa' ? 'کدنویسی فارکس (MQL5)' : 'Forex Algo', icon: TrendingUp },
    { id: 'portfolio', label: lang === 'fa' ? 'نمونه‌کارها' : 'Portfolio', icon: Briefcase },
    { id: 'tech-comparison', label: lang === 'fa' ? 'مقایسه تکنولوژی‌ها' : 'Tech Comparison', icon: Cpu },
    { id: 'calculator', label: lang === 'fa' ? 'محاسبه‌گر آنلاین' : 'Calculator', icon: Calculator },
    { id: 'why-us', label: lang === 'fa' ? 'چرا سورنا آی‌تی' : 'Why Sorena IT', icon: ShieldCheck },
    { id: 'faq', label: lang === 'fa' ? 'سوالات متداول' : 'FAQ', icon: HelpCircle },
    { id: 'contact', label: lang === 'fa' ? 'تماس و ثبت سفارش' : 'Contact', icon: Mail },
  ];

  return (
    <div className="relative pt-6 sm:pt-8 lg:pt-24 pb-8 overflow-hidden border-b border-amber-900/40 bg-gradient-to-b from-[#1c0e08] via-[#120804] to-[#0a0503]">
      {/* Ambient Gold Glow */}
      <div className="absolute top-0 right-1/4 w-96 h-96 bg-amber-600/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-yellow-600/10 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Breadcrumb Navigation */}
        <div className="flex items-center gap-2 text-xs text-stone-400 mb-4 flex-wrap">
          <button
            onClick={() => onNavigate('home')}
            className="flex items-center gap-1 hover:text-amber-300 transition-colors cursor-pointer"
          >
            <Home className="w-3.5 h-3.5 text-amber-400" />
            <span>{lang === 'fa' ? 'صفحه اصلی' : 'Home'}</span>
          </button>

          <Chevron className="w-3.5 h-3.5 text-stone-600" />

          <span className="text-amber-300 font-bold">{title}</span>
        </div>

        {/* Page Title & Main Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pb-6 border-b border-amber-900/40">
          <div className="space-y-2">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#0a0503] border border-amber-500/40 text-amber-200 text-xs font-mono font-bold shadow-sm shadow-amber-950/40">
                <SorenaLogo className="w-3.5 h-3.5" glow />
                <span>SORENA IT</span>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#241308] border border-amber-500/40 text-amber-300 text-xs font-bold shadow-sm shadow-amber-950/50">
                <Icon className="w-3.5 h-3.5 text-yellow-400 animate-pulse" />
                <span>{badge}</span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-white tracking-tight flex items-center gap-3">
              <span>{title}</span>
            </h1>

            <p className="text-sm sm:text-base text-stone-300 max-w-2xl leading-relaxed">
              {subtitle}
            </p>
          </div>

          {/* Direct WhatsApp Action for this page */}
          <div className="flex flex-wrap items-center gap-3 shrink-0">
            <a
              href={createWhatsAppUrl(
                lang === 'fa'
                  ? `سلام جناب مهندس کاویانی‌پور، از صفحه «${title}» وب‌سایت سورنا آی‌تی پیام می‌دهم و تمایل به مشاوره / ثبت سفارش دارم.`
                  : `Hello Mr. Kavianipoor, I am contacting you from the "${title}" section of the Sorena IT website for consultation / order inquiry.`
              )}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 via-yellow-400 to-amber-500 hover:from-amber-400 hover:to-yellow-300 text-stone-950 font-black text-xs shadow-lg shadow-amber-600/30 transition-all cursor-pointer"
            >
              <MessageCircle className="w-4 h-4 text-stone-950" />
              <span>{lang === 'fa' ? 'مشاوره واتس‌اپ:' : 'WhatsApp Consultation:'}</span>
              <span dir="ltr" className="font-mono font-bold">(+98)9128634908</span>
            </a>

            <button
              onClick={() => onNavigate('contact')}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#28150a] hover:bg-[#381d0e] text-amber-200 border border-amber-500/40 font-bold text-xs transition-all cursor-pointer"
            >
              <Zap className="w-3.5 h-3.5 text-yellow-400" />
              <span>{lang === 'fa' ? 'ثبت درخواست رسمی' : 'Submit Request'}</span>
            </button>
          </div>
        </div>

        {/* Quick Page Switcher Bar */}
        <div className="pt-4 flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
          <span className="text-xs text-stone-400 shrink-0 font-medium ml-2">
            {lang === 'fa' ? 'سایر صفحات:' : 'Other Pages:'}
          </span>
          {quickPages.map((page) => {
            const PageIcon = page.icon;
            const isActive = currentPage === page.id;
            return (
              <button
                key={page.id}
                onClick={() => onNavigate(page.id)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0 transition-all cursor-pointer ${
                  isActive
                    ? 'bg-gradient-to-r from-amber-500 to-yellow-500 text-stone-950 shadow-md shadow-amber-600/30 border border-amber-400 font-bold'
                    : 'bg-stone-900/80 hover:bg-amber-950/60 text-stone-300 hover:text-white border border-stone-800 hover:border-amber-800'
                }`}
              >
                <PageIcon className={`w-3.5 h-3.5 ${isActive ? 'text-stone-950' : 'text-amber-400'}`} />
                <span>{page.label}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
